import {
  getUserPortalSettingsForAdmin,
  setCustomLogoUrl,
  UserKeys,
} from '../../../api/users-api';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';

import { ModuleList } from '../../../shared/analytics/module-list';
import { CustomLogoSetup } from '../../../../types';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SharedTabHeader from '../../../shared/shared-tab-header';

export default function SetCustomLabel() {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const [lightModeImage, setLightModeImage] = useState<string>('');
  const [darkModeImage, setDarkModeImage] = useState<string>('');

  const { administrationRoles, changeLogRoles } = useUserSettings();

  const {
    data: customLabelData,
    isLoading: isCustomLabelDataLoading,
    isError: isCustomLabelDataError,
    error: customLabelDataError,
  } = useQuery({
    queryKey: [UserKeys.UserPortalSettings],
    queryFn: () => getUserPortalSettingsForAdmin(),
  });

  useEffect(() => {
    if (!isCustomLabelDataLoading && customLabelData) {
      setLightModeImage(customLabelData.logoUrl);
      setDarkModeImage(customLabelData.darkModeLogoUrl);
    }
  }, [isCustomLabelDataLoading, customLabelData]);

  useEffect(() => {
    if (isCustomLabelDataError) {
      handleErrors({
        error: customLabelDataError,
        snackbarMessage: Messages.Errors.Network.Loading('custom label data'),
      });
    }
  }, [isCustomLabelDataError, customLabelDataError]);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      setCustomLogoUrl(
        lightModeImage,
        darkModeImage,
        customLabelData!.colorHex,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Custom Label'),
      });

      queryClient.setQueryData<CustomLogoSetup | undefined>(
        [UserKeys.UserPortalSettings],
        (old) => {
          if (!old) return undefined;
          return {
            ...old,
            logoUrl: lightModeImage,
            darkModeLogoUrl: darkModeImage,
          };
        }
      );

      queryClient.invalidateQueries({
        queryKey: [UserKeys.UserPortalSettings],
      });

      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('custom label'),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.settings.update_custom_label, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.settings,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.UpdateCustomLabeling.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  if (isCustomLabelDataLoading) {
    return (
      <Stack gap={2}>
        <SharedTabHeader title="Set Custom Label" />
        <Box sx={{ px: 1 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add a company logo for both dark mode and light mode to be displayed
            within the User Portal.
          </Typography>

          <Stack direction="row" gap={2} my={2}>
            <Skeleton
              sx={{
                width: 400,
                height: 40,
              }}
              variant="rounded"
            />
            <Skeleton
              sx={{
                width: 400,
                height: 40,
              }}
              variant="rounded"
            />
          </Stack>
        </Box>
      </Stack>
    );
  }

  const hasUpdatePermission = isAbleToSeeUIElement(
    administrationRoles,
    AdministrationRoles.UpdateCustomLabeling.value
  );

  return (
    <Stack gap={2}>
      <SharedTabHeader title="Set Custom Label" />
      <Box sx={{ px: 1 }}>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Add a company logo for both dark mode and light mode to be displayed
          within the User Portal. The logo will have a 200px width and keep its
          aspect ratio, as shown in the preview below.
        </Typography>

        <Tooltip
          arrow
          title={
            !hasUpdatePermission
              ? `You need the "Can update Custom Labeling" role to make changes.`
              : ''
          }
        >
          <Stack direction="row" gap={2} my={2}>
            <Stack direction="column" gap={2}>
              <TextField
                id="logoUrl"
                label="Light Mode Image URL"
                disabled={!hasUpdatePermission}
                sx={{ width: 400 }}
                value={lightModeImage}
                onChange={(event) => setLightModeImage(event.target.value)}
              />
              {lightModeImage ? (
                <Box
                  component="img"
                  width={200}
                  height={80}
                  sx={{ objectFit: 'contain', objectPosition: 'left' }}
                  src={lightModeImage}
                />
              ) : null}
            </Stack>
            <Stack direction="column" gap={2}>
              <TextField
                id="darkModeLogoUrl"
                label="Dark Mode Image URL"
                disabled={!hasUpdatePermission}
                sx={{ width: 400 }}
                value={darkModeImage}
                onChange={(event) => setDarkModeImage(event.target.value)}
              />
              {darkModeImage ? (
                <Box
                  component="img"
                  width={200}
                  height={80}
                  sx={{ objectFit: 'contain', objectPosition: 'left' }}
                  src={darkModeImage}
                />
              ) : null}
            </Stack>
          </Stack>
        </Tooltip>

        <Button
          variant="contained"
          loading={isPending}
          type="submit"
          onClick={handleSubmit}
          disabled={!hasUpdatePermission}
        >
          Save
        </Button>
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={mutate}
          mutateLoading={isPending}
        />
      </Box>
    </Stack>
  );
}
